import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');
export const registerUser = (data) => api.post('/auth/register', data);

// Users
export const getUsers = () => api.get('/users');
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);

// Agents
export const getAgents = (params) => api.get('/agents', { params });
export const getAgent = (id) => api.get(`/agents/${id}`);
export const createAgent = (data) => api.post('/agents', data);
export const updateAgent = (id, data) => api.put(`/agents/${id}`, data);
export const deleteAgent = (id) => api.delete(`/agents/${id}`);

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);
export const addTaskStep = (id, step) => api.post(`/tasks/${id}/steps`, step);
export const updateTaskStep = (taskId, stepId, data) => api.put(`/tasks/${taskId}/steps/${stepId}`, data);
export const deleteTaskStep = (taskId, stepId) => api.delete(`/tasks/${taskId}/steps/${stepId}`);

// Queries
export const getQueries = (params) => api.get('/queries', { params });
export const getQuery = (id) => api.get(`/queries/${id}`);
export const createQuery = (data) => api.post('/queries', data);
export const rejectQuery = (id) => api.post(`/queries/${id}/reject`);
export const deleteQuery = (id) => api.delete(`/queries/${id}`);
export const convertQuery = (id, taskData) => api.post(`/queries/${id}/convert`, taskData);

// AI Analysis
export const analyzeVoice = (audio_base64, audio_format) => 
  api.post('/analyze/voice', { audio_base64, audio_format }, { timeout: 60000 });
export const analyzeText = (text) => api.post('/analyze/text', { text });

// Dashboard
export const getDashboard = () => api.get('/dashboard');

// Config
export const getFunctionalAreas = () => api.get('/config/functional-areas');
export const getFunctionalAreasFull = () => api.get('/config/functional-areas/full');
export const createFunctionalArea = (name) => api.post('/config/functional-areas', { name });
export const updateFunctionalArea = (id, name) => api.put(`/config/functional-areas/${id}`, { name });
export const deleteFunctionalArea = (id) => api.delete(`/config/functional-areas/${id}`);
export const getPriorities = () => api.get('/config/priorities');
export const getStatuses = () => api.get('/config/statuses');
export const getStatusesFull = () => api.get('/config/statuses/full');
export const createStatus = (name) => api.post('/config/statuses', { name });
export const updateStatus = (id, name) => api.put(`/config/statuses/${id}`, { name });
export const deleteStatus = (id) => api.delete(`/config/statuses/${id}`);

// Visibility toggle
export const toggleTaskVisibility = (taskId) => api.patch(`/tasks/${taskId}/visibility`);
export const toggleQueryVisibility = (queryId) => api.patch(`/queries/${queryId}/visibility`);

// Meetings
export const getMeetings = (params) => api.get('/meetings', { params });
export const getMeeting = (id) => api.get(`/meetings/${id}`);
export const createMeeting = (data) => api.post('/meetings', data);
export const updateMeeting = (id, data) => api.put(`/meetings/${id}`, data);
export const deleteMeeting = (id) => api.delete(`/meetings/${id}`);
export const toggleMeetingVisibility = (id) => api.patch(`/meetings/${id}/visibility`);

// Repositorio
export const getRepositorio = () => api.get('/repositorio');

// File Upload
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const uploadMultipleFiles = (files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  return api.post('/upload/multiple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Excel Import
export const importExcel = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// HR Agent
export const queryHRAgent = (question, session_id) => api.post('/hr-agent/query', { question, session_id });
export const getHRSessions = () => api.get('/hr-agent/sessions');
export const getHRSession = (id) => api.get(`/hr-agent/sessions/${id}`);
export const deleteHRSession = (id) => api.delete(`/hr-agent/sessions/${id}`);

// Seed
export const seedSuperadmin = () => api.post('/seed/superadmin');

export default api;
